import { Card, Table, Button, Row, Col, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { formatDate } from './utils';
import ReminderService from './modules/Reminder/Services/ReminderService';
import { IReminderListResponse, ReminderState } from './modules/Reminder/Types/Reminder';
import { NavLink } from 'react-router-dom';
import StudentService from './services/StudentService';
import DateRange from './components/Common/DateRange';

const { Option } = Select;

const stateMap: { [key in ReminderState]: string } = {
  PENDING: 'Beklemede',
  ACCEPT: 'Onaylandı',
  REJECT: 'Reddedildi',
  COMPLETED: 'Tamamlandı',
} as const;

export interface LiveLessonReminderFilter {
  startDate?: string;
  endDate?: string;
  state?: ReminderState[];
  accountId?: number | null;
}

export const buildLiveLessonReminderQuery = ({
  startDate,
  endDate,
  state = [],
  accountId,
}: LiveLessonReminderFilter) => {
  const parameters = new URLSearchParams({ entityNames: 'LiveLesson' });

  if (startDate) {
    parameters.set('startDate', startDate);
  }
  if (endDate) {
    parameters.set('endDate', endDate);
  }
  if (state.length > 0) {
    parameters.set('state', state.join(','));
  }
  if (accountId !== null && accountId !== undefined) {
    parameters.set('accountId', accountId.toString());
  }

  return `?${parameters.toString()}`;
};

const ReminderPage = () => {
  const [reminderList, setReminderList] = useState<IReminderListResponse[]>([]);
  const [stateFilter, setStateFilter] = useState<ReminderState[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [filter, setFilter] = useState<any>({
    startDate: dayjs().startOf('month').format('YYYY-MM-DDT00:00:00'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DDT23:59:59'),
  });

  const getStudents = () => {
    setLoading(true);
    StudentService.getAll()
      .then((response: any) => {
        const data = response.data;
        const options = data.map(
          (lesson: { id: number; firstName: string; lastName: string }) => ({
            label: `${lesson.firstName} ${lesson.lastName}`,
            value: lesson.id,
          })
        );
        setStudents(options);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  };

  useEffect(() => {
    getStudents();
  }, []);

  const getReminderList = useCallback(() => {
    ReminderService.getAll(
      buildLiveLessonReminderQuery({
        startDate: filter.startDate,
        endDate: filter.endDate,
        state: stateFilter,
        accountId: selectedStudent,
      })
    )
      .then((response: any) => {
        setReminderList(response.data.content ?? []);
      })
      .catch((e: Error) => {
        console.error(e);
      });
  }, [filter.endDate, filter.startDate, selectedStudent, stateFilter]);

  const handleStateUpdate = (state: ReminderState, reminder: IReminderListResponse) => {
    if (reminder.id === undefined) {
      return;
    }

    ReminderService.update(reminder.id, { state })
      .then(() => {
        getReminderList();
      })
      .catch((e: Error) => {
        console.error(e);
      });
  };

  useEffect(() => {
    getReminderList();
  }, [getReminderList]);

  useEffect(() => {
    setFilter((prev: any) => ({
      ...prev,
      startDate: dayjs(dateRange[0]).format('YYYY-MM-DDT00:00:00'),
      endDate: dayjs(dateRange[1]).format('YYYY-MM-DDT23:59:59'),
    }));
  }, [dateRange]);

  const handleStateChange = (value: ReminderState[]) => {
    setStateFilter(value);
  };

  const handleStudentChange = (value: number | undefined) => {
    setSelectedStudent(value ?? null);
  };

  const columns = [
    {
      title: 'Öğrenci',
      key: 'account',
      render: (reminder: any) => {
        return reminder.account ? (
          <>
            {reminder?.account?.firstName} {reminder?.account?.lastName}
          </>
        ) : null;
      },
    },
    {
      title: 'Ders Adı',
      key: 'lessonName',
      render: (reminder: any) => {
        const properties = reminder?.properties ? JSON.parse(reminder.properties) : null;
        return properties?.lessonName ? (
          <span>{properties.lessonName}</span>
        ) : null;
      },
    },
    {
      title: 'Not',
      dataIndex: 'content',
      key: 'content',
    },
    {
      title: 'Durum',
      dataIndex: 'state',
      key: 'state',
      render: (state: ReminderState | undefined, reminder: IReminderListResponse) => (
        <>
          <Select
            aria-label={`Canlı ders durumu ${reminder.id ?? ''}`}
            value={state}
            style={{ width: 150 }}
            onChange={(value: ReminderState) => handleStateUpdate(value, reminder)}
            disabled={state !== 'PENDING'}
          >
            {(Object.keys(stateMap) as ReminderState[]).map((key) => (
              <Option key={key} value={key}>
                {stateMap[key]}
              </Option>
            ))}
          </Select>
        </>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (text: string) => <b>{formatDate(text)}</b>,
    },
  ];

  return (
    <Card
      title="Canlı Ders"
      extra={
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <Select
              mode="multiple"
              placeholder="Durum seçin"
              aria-label="Durum filtresi"
              onChange={handleStateChange}
              allowClear
              size="large"
              style={{ width: 200 }}
            >
              {(Object.keys(stateMap) as ReminderState[]).map((key) => (
                <Option key={key} value={key}>
                  {stateMap[key]}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              showSearch
              placeholder="Öğrenci seçin"
              onChange={handleStudentChange}
              allowClear
              size="large"
              style={{ width: 200 }}
              loading={loading}
              options={students}
            />
          </Col>
          <Col>
            <DateRange onChangeDateRange={setDateRange} />
          </Col>
          <Col>
            <NavLink to="/">
              <Button size="large">Geri</Button>
            </NavLink>
          </Col>
        </Row>
      }
    >
      <Table
        columns={columns}
        dataSource={reminderList}
        rowKey={(record) => record.id?.toString() ?? `${record.entityName}-${record.entityId}-${record.reminderDate}`}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default ReminderPage;
